/**
 * Page publique de paiement Wave — SIMULATION SANDBOX (/wave-sandbox-pay/:linkId)
 * Réplique l'écran de paiement Wave pour tester le flux de bout en bout
 * avant activation de la vraie Checkout API.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, FlaskConical, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fxof = (v) => v == null ? '—' : `${Math.round(v).toLocaleString('fr-FR')} F CFA`;

export default function WaveSandboxPayPage() {
  const { linkId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    axios.get(`${API}/wave/checkout/pay/${linkId}`)
      .then((r) => { setInfo(r.data); if (r.data.status === 'complete') setPaid(true); })
      .catch((e) => setError(e.response?.data?.detail || 'Lien introuvable'));
  }, [linkId]);

  const pay = async () => {
    setPaying(true);
    try {
      await axios.post(`${API}/wave/checkout/pay/${linkId}/confirm`, {});
      setPaid(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Le paiement a échoué');
    }
    setPaying(false);
  };

  const expired = info?.status === 'expired';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(160deg, #00b4e6 0%, #0077b3 100%)' }} data-testid="wave-sandbox-pay-page">
      <div className="w-full max-w-md">
        {/* Bandeau sandbox */}
        <div className="mb-4 flex items-center justify-center gap-2 bg-amber-400/95 text-amber-950 text-xs font-bold px-4 py-2 rounded-full shadow" data-testid="sandbox-banner">
          <FlaskConical className="w-4 h-4" />
          ENVIRONNEMENT SANDBOX — AUCUN DÉBIT RÉEL
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header style Wave */}
          <div className="px-6 py-5 text-white" style={{ background: '#00b4e6' }}>
            <div className="text-2xl font-extrabold tracking-tight">wave</div>
            <div className="text-sky-100 text-xs mt-0.5">Paiement marchand — simulation</div>
          </div>

          <div className="p-6">
            {error && !info ? (
              <div className="text-center py-8" data-testid="pay-error-state">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <div className="font-semibold text-zinc-800">{error}</div>
              </div>
            ) : !info ? (
              <div className="text-center py-10 text-zinc-400"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>
            ) : paid ? (
              <div className="text-center py-6" data-testid="pay-success-state">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
                <div className="text-xl font-bold text-zinc-900">Paiement effectué ✓</div>
                <div className="text-sm text-zinc-500 mt-1.5">
                  {fxof(info.amount)} versés à <strong>{info.businessName}</strong>
                </div>
                <div className="text-xs text-zinc-400 mt-1">Facture {info.invoiceNumber} — lettrage automatique effectué</div>
                <div className="mt-5 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                  En production, ce paiement serait confirmé par le webhook Wave en temps réel.
                </div>
              </div>
            ) : expired ? (
              <div className="text-center py-8" data-testid="pay-expired-state">
                <XCircle className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <div className="font-semibold text-zinc-800">Ce lien de paiement a expiré</div>
                <div className="text-sm text-zinc-500 mt-1">Contactez {info.businessName} pour obtenir un nouveau lien.</div>
              </div>
            ) : (
              <div data-testid="pay-open-state">
                <div className="text-center mb-5">
                  <div className="text-sm text-zinc-500">Vous payez</div>
                  <div className="text-4xl font-extrabold text-zinc-900 my-1.5" data-testid="pay-amount">{fxof(info.amount)}</div>
                  <div className="text-sm text-zinc-600">à <strong>{info.businessName}</strong></div>
                </div>

                <div className="rounded-xl border divide-y text-sm mb-5">
                  <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">Facture</span><span className="font-semibold">{info.invoiceNumber}</span></div>
                  <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">Client</span><span className="font-semibold">{info.companyName}</span></div>
                  {info.restrictedMobile && (
                    <div className="flex justify-between px-4 py-2.5 items-center">
                      <span className="text-zinc-500 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> Réservé au n°</span>
                      <span className="font-semibold">••• •• {info.restrictedMobile}</span>
                    </div>
                  )}
                </div>

                {error && <div className="mb-3 text-sm text-red-600 text-center" data-testid="pay-inline-error">{error}</div>}

                <button
                  data-testid="confirm-sandbox-payment-btn"
                  onClick={pay}
                  disabled={paying}
                  className="w-full py-3.5 rounded-full text-white font-bold text-base transition-opacity disabled:opacity-60"
                  style={{ background: '#00b4e6' }}
                >
                  {paying ? <Loader2 className="w-5 h-5 animate-spin inline" /> : 'Payer avec Wave (simulation)'}
                </button>
                <div className="text-center text-[11px] text-zinc-400 mt-3">
                  Simulation : en production, cette étape s'effectue dans l'application Wave du client.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
